import type { Customer } from '@store/slices/posCustomersSlice.tsx';
import type { ManualDiscountDraft, PosSaleLine } from '@erp/pos/types';
import type { QuoteLineCreate, QuoteUpdatePayload } from '../types';
import { computeLineAmounts } from '@erp/pos/components/discountMath';

const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const buildQuoteLines = (lines: PosSaleLine[], currencyCode: string): QuoteLineCreate[] =>
  lines.map((line, index) => ({
    item_id: line.item.id,
    variant_id: line.variantId ?? null,
    item_type: line.item.item_type,
    sku: line.item.sku ?? null,
    name_snapshot: line.item.name,
    image_url_snapshot: line.item.thumbnail_url ?? null,
    quantity: line.quantity,
    unit_price: toNumber(line.item.price?.value),
    discount_total: computeLineAmounts(line).manualDiscountAmount,
    currency_code: line.item.price?.currency ?? currencyCode,
    professional_id: line.professionalId ?? null,
    line_key: line.lineKey ?? `pos-line-${index + 1}`,
    options_snapshot: {
      sku: line.item.sku ?? null,
      itemType: line.item.item_type,
      variantId: line.variantId ?? null,
      notes: line.notes ?? null,
      subscriptionId: line.subscriptionId ?? null,
      useSubscription: Boolean(line.useSubscription),
    },
    manual_discount_snapshot: line.manualDiscount ? { ...line.manualDiscount } : null,
    commission_snapshot: line.commission ? { ...line.commission } : null,
    subscription_snapshot: {
      subscriptionId: line.subscriptionId ?? null,
      useSubscription: Boolean(line.useSubscription),
      available: line.subscriptionAvailable ?? null,
      unlimited: Boolean(line.subscriptionUnlimited),
      name: line.subscriptionName ?? null,
    },
    flags_snapshot: line.flags ? { ...line.flags } : null,
    metadata: { source: 'pos', notes: line.notes ?? null },
  }));

export const buildCustomerSnapshot = (customer?: Customer | null): Record<string, unknown> | null => {
  if (!customer) return null;
  return {
    first_name: customer.first_name,
    last_name: customer.last_name ?? null,
    name: [customer.first_name, customer.last_name].filter(Boolean).join(' '),
    email: customer.email ?? null,
    phone: customer.phone ?? null,
  };
};

export const buildPosQuoteUpdatePayload = (
  lines: PosSaleLine[],
  customer: Customer | null,
  customerId: number | null,
  currencyCode: string,
  orderDiscount?: ManualDiscountDraft | null,
  shipping?: { methodId: number; methodName: string; amount: number; currency: string; addressId?: string | null; address?: unknown; manualAmount?: number | null } | null,
): QuoteUpdatePayload => ({
  customer_id: customerId,
  customer_snapshot: buildCustomerSnapshot(customer),
  currency_code: currencyCode,
  order_discount_snapshot: orderDiscount ? { ...orderDiscount } : null,
  shipping_snapshot: shipping ?? null,
  shipping_total: shipping?.amount ?? 0,
  pos_draft: {
    customerId,
    currencyCode,
    items: lines.map((line) => ({
      itemId: line.item.id,
      variantId: line.variantId ?? null,
      name: line.item.name,
      sku: line.item.sku ?? null,
      itemType: line.item.item_type,
      quantity: line.quantity,
      unitPrice: toNumber(line.item.price?.value),
      currency_code: line.item.price?.currency ?? currencyCode,
      professionalId: line.professionalId ?? null,
      lineKey: line.lineKey ?? null,
      notes: line.notes ?? null,
      manualDiscount: line.manualDiscount ?? null,
      commission: line.commission ?? null,
      flags: line.flags ?? null,
      options: {
        variantId: line.variantId ?? null,
        subscriptionId: line.subscriptionId ?? null,
        useSubscription: Boolean(line.useSubscription),
      },
    })),
    orderDiscount: orderDiscount ?? null,
    shipping: shipping ?? null,
  },
  lines: buildQuoteLines(lines, currencyCode),
});
